import React from 'react';
import PropertiesPanel from './properties/PropertiesPanel';
import ColorPickerWithPresets from './common/ColorPickerWithPresets';

function RightSidebar({
                          selectedElement,
                          setSelectedElement,
                          selectedCell,
                          setSelectedCell,
                          grid,
                          setGrid,
                          currentUid,
                          shareUrl,
                          apiEnabled,
                          onImageUpload,
                          showNotification,
                          previewMode
                      }) {
    const updateElement = (updates) => {
        if (!selectedElement) return;

        const newGrid = [...grid];

        const findAndUpdate = (grid) => {
            for (const row of grid) {
                for (const cell of row.cells) {
                    const element = cell.elements?.find(el => el.id === selectedElement.id);
                    if (element) {
                        Object.assign(element, updates);
                        setSelectedElement({ ...element, ...updates });
                        return true;
                    }
                    if (cell.nestedGrid && findAndUpdate(cell.nestedGrid)) {
                        return true;
                    }
                }
            }
            return false;
        };

        findAndUpdate(newGrid);
        setGrid(newGrid);
    };

    const updateCellWidth = (newWidthPx) => {
        if (!selectedCell || selectedCell.isNested) {
            // Pour les cellules imbriquées, utiliser l'ancienne méthode
            updateCellStyle({ width: `${newWidthPx}px` });
            return;
        }

        const { rowIndex, cellIndex } = selectedCell;
        const newGrid = [...grid];
        const row = newGrid[rowIndex];
        const cells = row.cells;
        const totalColumns = cells.length;

        // Si c'est une cellule seule, on ne peut pas redimensionner
        if (totalColumns === 1) {
            updateCellStyle({ width: `${newWidthPx}px` });
            return;
        }

        // Récupérer les largeurs actuelles
        const getCurrentWidth = (cell) => {
            const width = cell.style?.width;
            if (!width || width === 'auto') return 200; // Valeur par défaut
            return parseInt(width);
        };

        // Calculer la largeur totale disponible
        const totalWidth = cells.reduce((sum, cell) => sum + getCurrentWidth(cell), 0);

        const currentWidth = getCurrentWidth(cells[cellIndex]);
        const diff = newWidthPx - currentWidth;

        // Largeur minimale par cellule
        const MIN_WIDTH = 50;

        // Calculer la largeur maximale : totalWidth - (nombre d'autres cellules × MIN_WIDTH)
        const MAX_WIDTH = totalWidth - ((totalColumns - 1) * MIN_WIDTH);

        // Vérifier que la nouvelle largeur est dans les limites
        if (newWidthPx < MIN_WIDTH) {
            return;
        }

        if (newWidthPx > MAX_WIDTH) {
            return;
        }

        // Stratégie : ajuster prioritairement la cellule de droite, sinon celle de gauche
        // Cela permet de garder un comportement prévisible
        let targetCellIndex;
        if (cellIndex < totalColumns - 1) {
            // Il y a une cellule à droite
            targetCellIndex = cellIndex + 1;
        } else if (cellIndex > 0) {
            // Il y a une cellule à gauche (on est sur la dernière cellule)
            targetCellIndex = cellIndex - 1;
        } else {
            // Cas impossible normalement (cellule seule déjà géré)
            updateCellStyle({ width: `${newWidthPx}px` });
            return;
        }

        const targetCellWidth = getCurrentWidth(cells[targetCellIndex]);
        const newTargetCellWidth = targetCellWidth - diff;

        // Vérifier que la cellule cible ne sera pas trop petite
        if (newTargetCellWidth < MIN_WIDTH) {
            // Si la cellule cible devient trop petite, on essaie de distribuer le diff sur les autres cellules
            // Calcul de l'espace disponible dans les autres cellules
            let availableSpace = 0;
            const otherCells = [];

            for (let i = 0; i < totalColumns; i++) {
                if (i !== cellIndex && i !== targetCellIndex) {
                    const cellWidth = getCurrentWidth(cells[i]);
                    const spaceAvailable = cellWidth - MIN_WIDTH;
                    if (spaceAvailable > 0) {
                        availableSpace += spaceAvailable;
                        otherCells.push({ index: i, width: cellWidth, available: spaceAvailable });
                    }
                }
            }

            // Calculer le déficit (ce qui manque pour atteindre MIN_WIDTH sur la cellule cible)
            const deficit = MIN_WIDTH - newTargetCellWidth;

            if (availableSpace >= deficit) {
                // On peut redistribuer le déficit sur les autres cellules
                cells[cellIndex].style = cells[cellIndex].style || {};
                cells[cellIndex].style.width = `${newWidthPx}px`;

                cells[targetCellIndex].style = cells[targetCellIndex].style || {};
                cells[targetCellIndex].style.width = `${MIN_WIDTH}px`;

                // Distribuer le déficit proportionnellement sur les autres cellules
                let remainingDeficit = deficit;
                otherCells.forEach((cell, idx) => {
                    const proportion = cell.available / availableSpace;
                    const reduction = idx === otherCells.length - 1
                        ? remainingDeficit // Dernière cellule prend le reste pour éviter les erreurs d'arrondi
                        : Math.round(proportion * deficit);

                    const newWidth = cell.width - reduction;
                    cells[cell.index].style = cells[cell.index].style || {};
                    cells[cell.index].style.width = `${newWidth}px`;

                    remainingDeficit -= reduction;
                });

                setGrid(newGrid);
                return;
            } else {
                // Pas assez d'espace disponible, on refuse le redimensionnement
                return;
            }
        }

        // Cas simple : on peut ajuster directement la cellule cible
        if (!cells[cellIndex].style) {
            cells[cellIndex].style = {};
        }
        if (!cells[targetCellIndex].style) {
            cells[targetCellIndex].style = {};
        }

        cells[cellIndex].style.width = `${newWidthPx}px`;
        cells[targetCellIndex].style.width = `${newTargetCellWidth}px`;

        setGrid(newGrid);
    };

    const updateCellStyle = (updates) => {
        if (!selectedCell) return;

        const newGrid = grid.map((row, rIndex) => {
            if (selectedCell.isNested) {
                // Cellule imbriquée
                if (rIndex !== selectedCell.parentRowIndex) return row;
                return {
                    ...row,
                    cells: row.cells.map((cell, cIndex) => {
                        if (cIndex !== selectedCell.parentCellIndex) return cell;
                        return {
                            ...cell,
                            nestedGrid: cell.nestedGrid.map((nestedRow, nrIndex) => {
                                if (nrIndex !== selectedCell.nestedRowIndex) return nestedRow;
                                return {
                                    ...nestedRow,
                                    cells: nestedRow.cells.map((nestedCell, ncIndex) => {
                                        if (ncIndex !== selectedCell.nestedCellIndex) return nestedCell;
                                        return {
                                            ...nestedCell,
                                            style: { ...nestedCell.style, ...updates }
                                        };
                                    })
                                };
                            })
                        };
                    })
                };
            } else {
                // Cellule principale
                if (rIndex !== selectedCell.rowIndex) return row;
                return {
                    ...row,
                    cells: row.cells.map((cell, cIndex) => {
                        if (cIndex !== selectedCell.cellIndex) return cell;
                        return {
                            ...cell,
                            style: { ...cell.style, ...updates }
                        };
                    })
                };
            }
        });

        setGrid(newGrid);
    };

    const getCellStyle = () => {
        if (!selectedCell) return {};

        if (selectedCell.isNested) {
            const parentRow = grid[selectedCell.parentRowIndex];
            if (!parentRow) return {};
            const parentCell = parentRow.cells[selectedCell.parentCellIndex];
            if (!parentCell || !parentCell.nestedGrid) return {};
            const nestedRow = parentCell.nestedGrid[selectedCell.nestedRowIndex];
            if (!nestedRow) return {};
            const nestedCell = nestedRow.cells[selectedCell.nestedCellIndex];
            return nestedCell?.style || {};
        } else {
            const row = grid[selectedCell.rowIndex];
            if (!row) return {};
            const cell = row.cells[selectedCell.cellIndex];
            return cell?.style || {};
        }
    };

    if (previewMode) {
        return null;
    }

    return (
        <div className="sidebar-right">
            <div className="properties-panel">
                {apiEnabled && shareUrl && (
                    <div className="api-panel">
                        <h3>🔗 Partage</h3>
                        <div className="share-url">
                            <strong>UID:</strong> {currentUid}<br/>
                            <strong>URL:</strong> <a href={shareUrl} target="_blank" rel="noopener noreferrer">{shareUrl}</a>
                        </div>
                    </div>
                )}

                {selectedElement ? (
                    <PropertiesPanel
                        element={selectedElement}
                        onUpdate={updateElement}
                        onImageUpload={onImageUpload}
                        apiEnabled={apiEnabled}
                        showNotification={showNotification}
                    />
                ) : selectedCell ? (
                    <div>
                        <h2>🔲 {selectedCell.isNested ? 'Cellule Imbriquée' : 'Cellule'}</h2>
                        {selectedCell.isNested && (
                            <p style={{ fontSize: '12px', color: '#666', marginBottom: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '6px' }}>
                                📍 Position: Ligne {selectedCell.nestedRowIndex + 1}, Colonne {selectedCell.nestedCellIndex + 1}
                            </p>
                        )}
                        <div className="form-group">
                            <label>Largeur (px)</label>
                            <input
                                key={`width-${JSON.stringify(getCellStyle().width)}`}
                                type="number"
                                min="50"
                                max={(() => {
                                    if (!selectedCell || selectedCell.isNested) return 800;
                                    const row = grid[selectedCell.rowIndex];
                                    if (!row) return 800;
                                    const cells = row.cells;
                                    const totalColumns = cells.length;
                                    if (totalColumns === 1) return 800;

                                    // Calculer la largeur totale
                                    const getCurrentWidth = (cell) => {
                                        const width = cell?.style?.width;
                                        if (!width || width === 'auto') return 200;
                                        return parseInt(width);
                                    };
                                    const totalWidth = cells.reduce((sum, cell) => sum + getCurrentWidth(cell), 0);

                                    // MAX = totalWidth - ((totalColumns - 1) × 50)
                                    return totalWidth - ((totalColumns - 1) * 50);
                                })()}
                                defaultValue={(() => {
                                    const style = getCellStyle();
                                    const width = style.width;
                                    const num = width && width !== 'auto' ? parseFloat(width) : '';
                                    return num && !isNaN(num) ? Math.round(num) : '';
                                })()}
                                onBlur={(e) => {
                                    const value = e.target.value;
                                    if (value === '' || value === '0') {
                                        updateCellStyle({ width: 'auto' });
                                    } else {
                                        const numValue = parseInt(value);

                                        // Calculer le max dynamiquement
                                        let maxWidth = 800;
                                        if (selectedCell && !selectedCell.isNested) {
                                            const row = grid[selectedCell.rowIndex];
                                            if (row) {
                                                const cells = row.cells;
                                                const totalColumns = cells.length;
                                                if (totalColumns > 1) {
                                                    const getCurrentWidth = (cell) => {
                                                        const width = cell?.style?.width;
                                                        if (!width || width === 'auto') return 200;
                                                        return parseInt(width);
                                                    };
                                                    const totalWidth = cells.reduce((sum, cell) => sum + getCurrentWidth(cell), 0);
                                                    maxWidth = totalWidth - ((totalColumns - 1) * 50);
                                                }
                                            }
                                        }

                                        // Appliquer le clamping entre min (50) et max
                                        const finalValue = Math.max(50, Math.min(maxWidth, numValue));
                                        updateCellWidth(finalValue);

                                        // Mettre à jour le champ si la valeur a été corrigée
                                        if (finalValue !== numValue) {
                                            e.target.value = finalValue;
                                        }
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.target.blur();
                                    }
                                }}
                                placeholder="auto"
                            />
                            <small style={{ fontSize: '11px', color: '#7f8c8d', display: 'block', marginTop: '4px' }}>
                                Min: 50px - Max: {(() => {
                                if (!selectedCell || selectedCell.isNested) return '800px';
                                const row = grid[selectedCell.rowIndex];
                                if (!row) return '800px';
                                const cells = row.cells;
                                const totalColumns = cells.length;
                                if (totalColumns === 1) return '800px';

                                const getCurrentWidth = (cell) => {
                                    const width = cell?.style?.width;
                                    if (!width || width === 'auto') return 200;
                                    return parseInt(width);
                                };
                                const totalWidth = cells.reduce((sum, cell) => sum + getCurrentWidth(cell), 0);
                                return `${totalWidth - ((totalColumns - 1) * 50)}px`;
                            })()}
                            </small>
                        </div>
                        <div className="form-group">
                            <label>Padding (px)</label>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '50px 60px 50px',
                                gridTemplateRows: 'auto auto auto',
                                gap: '4px',
                                alignItems: 'center',
                                justifyItems: 'center'
                            }}>
                                {/* Haut - centré sur 3 colonnes */}
                                <div style={{ gridColumn: '2' }}>
                                    <input
                                        type="number"
                                        min="0"
                                        value={(() => { const v = parseInt(getCellStyle().paddingTop); return isNaN(v) ? 15 : v; })()}
                                        onChange={(e) => updateCellStyle({ paddingTop: `${e.target.value}px` })}
                                        style={{ width: '50px', textAlign: 'center', padding: '4px' }}
                                        title="Haut"
                                    />
                                </div>
                                {/* Gauche */}
                                <div style={{ gridColumn: '1', gridRow: '2' }}>
                                    <input
                                        type="number"
                                        min="0"
                                        value={(() => { const v = parseInt(getCellStyle().paddingLeft); return isNaN(v) ? 15 : v; })()}
                                        onChange={(e) => updateCellStyle({ paddingLeft: `${e.target.value}px` })}
                                        style={{ width: '50px', textAlign: 'center', padding: '4px' }}
                                        title="Gauche"
                                    />
                                </div>
                                {/* Centre - icône visuelle */}
                                <div style={{
                                    gridColumn: '2',
                                    gridRow: '2',
                                    width: '36px',
                                    height: '36px',
                                    border: '2px dashed #ccc',
                                    borderRadius: '4px',
                                    background: '#f9f9f9'
                                }} />
                                {/* Droite */}
                                <div style={{ gridColumn: '3', gridRow: '2' }}>
                                    <input
                                        type="number"
                                        min="0"
                                        value={(() => { const v = parseInt(getCellStyle().paddingRight); return isNaN(v) ? 15 : v; })()}
                                        onChange={(e) => updateCellStyle({ paddingRight: `${e.target.value}px` })}
                                        style={{ width: '50px', textAlign: 'center', padding: '4px' }}
                                        title="Droite"
                                    />
                                </div>
                                {/* Bas - centré sur 3 colonnes */}
                                <div style={{ gridColumn: '2', gridRow: '3' }}>
                                    <input
                                        type="number"
                                        min="0"
                                        value={(() => { const v = parseInt(getCellStyle().paddingBottom); return isNaN(v) ? 15 : v; })()}
                                        onChange={(e) => updateCellStyle({ paddingBottom: `${e.target.value}px` })}
                                        style={{ width: '50px', textAlign: 'center', padding: '4px' }}
                                        title="Bas"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Alignement vertical</label>
                            <select
                                value={getCellStyle().verticalAlign || 'top'}
                                onChange={(e) => updateCellStyle({ verticalAlign: e.target.value })}
                            >
                                <option value="top">Haut</option>
                                <option value="middle">Centre</option>
                                <option value="bottom">Bas</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                                <ColorPickerWithPresets
                                    label="Couleur de fond"
                                    value={getCellStyle().backgroundColor || '#ffffff'}
                                    onChange={(color) => updateCellStyle({ backgroundColor: color })}
                                />
                                <button
                                    onClick={() => updateCellStyle({ backgroundColor: '' })}
                                    style={{
                                        padding: '6px 12px',
                                        fontSize: '12px',
                                        background: '#e74c3c',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        height: '32px'
                                    }}
                                    title="Rendre transparent"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>

                        {/* Bordures */}
                        <div className="form-group">
                            <label>Bordures</label>
                            {/* Sélecteur visuel de bordures */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '20px 50px 20px',
                                gridTemplateRows: '20px 50px 20px',
                                gap: '2px',
                                alignItems: 'center',
                                justifyItems: 'center',
                                marginBottom: '10px'
                            }}>
                                {/* Bordure Haut */}
                                <div
                                    onClick={() => updateCellStyle({ borderTop: !getCellStyle().borderTop })}
                                    title="Bordure haut"
                                    style={{
                                        gridColumn: '2',
                                        gridRow: '1',
                                        width: '100%',
                                        height: '8px',
                                        background: getCellStyle().borderTop ? '#3498db' : '#ddd',
                                        borderRadius: '2px',
                                        cursor: 'pointer',
                                        transition: 'background 0.2s'
                                    }}
                                />
                                {/* Bordure Gauche */}
                                <div
                                    onClick={() => updateCellStyle({ borderLeft: !getCellStyle().borderLeft })}
                                    title="Bordure gauche"
                                    style={{
                                        gridColumn: '1',
                                        gridRow: '2',
                                        width: '8px',
                                        height: '100%',
                                        background: getCellStyle().borderLeft ? '#3498db' : '#ddd',
                                        borderRadius: '2px',
                                        cursor: 'pointer',
                                        transition: 'background 0.2s'
                                    }}
                                />
                                {/* Centre - représentation de la cellule */}
                                <div style={{
                                    gridColumn: '2',
                                    gridRow: '2',
                                    width: '100%',
                                    height: '100%',
                                    background: '#f9f9f9',
                                    borderRadius: '2px'
                                }} />
                                {/* Bordure Droite */}
                                <div
                                    onClick={() => updateCellStyle({ borderRight: !getCellStyle().borderRight })}
                                    title="Bordure droite"
                                    style={{
                                        gridColumn: '3',
                                        gridRow: '2',
                                        width: '8px',
                                        height: '100%',
                                        background: getCellStyle().borderRight ? '#3498db' : '#ddd',
                                        borderRadius: '2px',
                                        cursor: 'pointer',
                                        transition: 'background 0.2s'
                                    }}
                                />
                                {/* Bordure Bas */}
                                <div
                                    onClick={() => updateCellStyle({ borderBottom: !getCellStyle().borderBottom })}
                                    title="Bordure bas"
                                    style={{
                                        gridColumn: '2',
                                        gridRow: '3',
                                        width: '100%',
                                        height: '8px',
                                        background: getCellStyle().borderBottom ? '#3498db' : '#ddd',
                                        borderRadius: '2px',
                                        cursor: 'pointer',
                                        transition: 'background 0.2s'
                                    }}
                                />
                            </div>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                                <div style={{ flex: '0 0 80px' }}>
                                    <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Epaisseur</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="10"
                                        value={parseInt(getCellStyle().borderWidth) || 1}
                                        onChange={(e) => updateCellStyle({ borderWidth: `${e.target.value}px` })}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <ColorPickerWithPresets
                                    label="Couleur"
                                    value={getCellStyle().borderColor || '#000000'}
                                    onChange={(color) => updateCellStyle({ borderColor: color })}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div>
                        <h2>Propriétés</h2>
                        <p style={{ color: '#95a5a6', fontSize: '14px' }}>
                            Sélectionnez un élément ou une cellule pour modifier ses propriétés
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default RightSidebar;
